import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import Backend from 'i18next-http-backend';
import i18nIsoCountries from 'i18n-iso-countries';
import i18nIsoLangs from '@cospired/i18n-iso-languages';

import { LS_LOCALE_KEY } from 'utils/localStorage';

import { setYupLocale } from './yupLocale';

// We should migrate to using 2-letter base locales except to differentiate.
// e.g. 'en' would be the default for all English locales, but we can add 'en-GB'
// if we want to support British English separately
const defaultLocale = 'en';

// Supported locales (BCP 47), use '-' as separator for region variants
const supportedLocales = ['en', 'ru'];

const resolveLocale = (locale: string): string => {
    // Use '-' as our default separator as per BCP 47,
    // but also accept '_' for compatibility
    const normalizedLocale = locale.replace('_', '-').toLowerCase();

    // Exact match, e.g. 'es' or 'es-MX'
    if (supportedLocales.includes(normalizedLocale)) {
        return normalizedLocale;
    }

    // Match the language, e.g. 'es' or 'es-MX' for 'es-ES'
    const localeLang = normalizedLocale.split('-')[0];
    if (supportedLocales.includes(localeLang)) {
        return localeLang;
    }

    // If no exact match on lang (e.g. only contains 'es-MX' but not 'es'),
    // try to find a locale that starts with the same lang
    const localeWithMatchingLang = supportedLocales.find((supportedLocale) => supportedLocale.startsWith(localeLang));
    if (localeWithMatchingLang) {
        return localeWithMatchingLang;
    }

    return defaultLocale;
};

let currentLocale = resolveLocale(localStorage.getItem(LS_LOCALE_KEY) ?? '');
export const getCurrentLocale = () => currentLocale;
export const getCurrentLocaleISO2Lang = () => currentLocale.substring(0, 2).toLowerCase();

const propagateLocale = async () => {
    const langsLocale = await import(`@cospired/i18n-iso-languages/langs/${i18n.language}.json`);
    i18nIsoLangs.registerLocale(langsLocale);

    const countriesLocale = await import(`i18n-iso-countries/langs/${i18n.language}.json`);
    i18nIsoCountries.registerLocale(countriesLocale.default);
    setYupLocale(i18n.t);
};

export const changeLocale = (locale?: string): void => {
    const resolvedLocale = resolveLocale(locale ?? currentLocale);

    if (locale) {
        localStorage.setItem(LS_LOCALE_KEY, resolvedLocale);
        i18n.changeLanguage(resolvedLocale).then(() => propagateLocale());
        currentLocale = resolvedLocale;
    }
};

export const localeLowerCase = (str: string) => {
    return str.toLocaleLowerCase(getCurrentLocale());
};

export const localeCapitalize = (str: string) => {
    const locale = getCurrentLocale();

    const firstChar = str.substring(0, 1).toLocaleUpperCase(locale);
    const restOfString = str.substring(1);

    return `${firstChar}${restOfString}`;
};

i18n.use(initReactI18next)
    // Load translation using http
    // (see /public/locales, learn more: https://github.com/i18next/i18next-http-backend)
    .use(Backend)
    // for all options read: https://www.i18next.com/overview/configuration-options
    .init({
        lng: currentLocale,
        fallbackLng: defaultLocale,
        interpolation: {
            // Not needed for react as it escapes by default
            escapeValue: false,
        },
        react: {
            useSuspense: true,
        },
    })
    .then(() => propagateLocale());

export default i18n;
